if (!customElements.get('product-media')) {
  class ProductMedia extends HTMLElement {
    constructor() {
      super();

      this.index = 0;
      this.productModalHTML = {};
      this.selectors = {
        header: 'sticky-header',
        slider: '[data-slider]',
        track: '[data-track]',
        thumbs: '[data-thumbs]',
        mediaItem: '[data-media-item]',
        mediaColor: 'data-media-color',
        thumbnail: '[data-thumbnail]',
        progress: '[data-progress]',
        thumbsPrev: '[data-thumbs-prev]',
        thumbsNext: '[data-thumbs-next]'
      };
      this.filterMediaBy = this.dataset.galleryFilter;
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
    }

    connectedCallback() {
      this.slider = this.querySelector(this.selectors.slider);
      this.track = this.querySelector(this.selectors.track);
      this.thumbsViewport = this.querySelector(this.selectors.thumbs);
      this.progressFill = this.querySelector(this.selectors.progress);
      this.prevBtn = this.querySelector(this.selectors.thumbsPrev);
      this.nextBtn = this.querySelector(this.selectors.thumbsNext);

      this.refreshItems();
      this.index =
        Number(
          this.visibleSlides.find(slide =>
            slide.hasAttribute('data-selected')
          )?.dataset.index
        ) || 0;

      this.onScroll = this.onScroll.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onThumbScroll = this.onThumbScroll.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);

      this.addEventListener('click', this.onClick, true);
      this.slider?.addEventListener('scroll', this.onScroll, {
        passive: true
      });
      this.thumbsViewport?.addEventListener(
        'scroll',
        this.onThumbScroll,
        { passive: true }
      );
      this.prevBtn?.addEventListener('click', () =>
        this.scrollThumbs(-1)
      );
      this.nextBtn?.addEventListener('click', () =>
        this.scrollThumbs(1)
      );
      this.desktopQuery.addEventListener(
        'change',
        this.onBreakpointChange
      );

      this.resizeObserver = new ResizeObserver(() => {
        this.syncDesktopHeight();
        if (!this.isStageDesktop()) {
          this.goTo(this.index, false);
        }
      });
      if (this.slider) this.resizeObserver.observe(this.slider);
      this.resizeObserver.observe(this);

      this.initLightbox();
      this.syncDesktopHeight();
      this.goTo(this.index, false);
      requestAnimationFrame(() => this.syncDesktopHeight());
    }

    disconnectedCallback() {
      this.desktopQuery.removeEventListener(
        'change',
        this.onBreakpointChange
      );
      this.resizeObserver?.disconnect();
    }

    isDesktop() {
      return this.desktopQuery.matches;
    }

    isStageDesktop() {
      return (
        this.isDesktop() &&
        !this.hasAttribute('data-slider-desktop-disabled')
      );
    }

    get visibleSlides() {
      return Array.from(
        this.querySelectorAll(this.selectors.mediaItem)
      ).filter(slide => !slide.classList.contains('hidden'));
    }

    get visibleThumbs() {
      return Array.from(
        this.querySelectorAll(this.selectors.thumbnail)
      ).filter(thumb => !thumb.classList.contains('hidden'));
    }

    refreshItems() {
      this.visibleSlides.forEach((slide, index) => {
        slide.dataset.index = String(index);
      });
      this.visibleThumbs.forEach((thumb, index) => {
        thumb.dataset.index = String(index);
      });
    }

    initLightbox() {
      if (typeof PhotoSwipeLightbox === 'undefined') return;

      this.lightbox = new PhotoSwipeLightbox({
        gallery: this,
        children: 'a[data-pswp-image]',
        pswpModule: PhotoSwipe
      });
      this.lightbox.init();
    }

    onClick(event) {
      if (!this.isDesktop()) {
        const opener = event.target.closest('a[data-pswp-image]');
        if (opener && this.contains(opener)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
      }

      const thumb = event.target.closest(this.selectors.thumbnail);
      if (
        !thumb ||
        !this.contains(thumb) ||
        thumb.classList.contains('hidden')
      ) {
        return;
      }

      event.preventDefault();
      this.goTo(Number(thumb.dataset.index));
    }

    onBreakpointChange() {
      requestAnimationFrame(() => {
        this.syncDesktopHeight();
        this.goTo(this.index, false);
      });
    }

    onScroll() {
      if (this.isStageDesktop() || !this.slider) return;

      const width = this.slider.clientWidth;
      if (!width) return;

      const nextIndex = Math.round(this.slider.scrollLeft / width);
      if (nextIndex === this.index) return;

      this.index = this.clampIndex(nextIndex);
      this.syncActiveState();
    }

    onThumbScroll() {
      this.updateArrows();
    }

    clampIndex(index) {
      const max = Math.max(this.visibleSlides.length - 1, 0);
      return Math.max(0, Math.min(index, max));
    }

    goTo(index, animate = true) {
      this.index = this.clampIndex(index);

      if (this.isStageDesktop()) {
        if (this.slider) this.slider.scrollLeft = 0;
      } else if (this.slider) {
        const left = this.index * this.slider.clientWidth;
        this.slider.scrollTo({
          left,
          behavior: animate ? 'smooth' : 'auto'
        });
      }

      this.syncActiveState();
      this.scrollThumbIntoView(animate);
    }

    syncActiveState() {
      this.visibleSlides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        if (active) this.unveilSlide(slide);
      });
      this.visibleThumbs.forEach((thumb, index) => {
        const active = index === this.index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.updateProgress();
      this.updateArrows();
      this.syncThumbsMetrics();
      this.syncVideos();
    }

    unveilSlide(slide) {
      slide
        .querySelectorAll('img.lazyload, img.lazyloading')
        .forEach(img => {
          if (window.lazySizes?.loader?.unveil) {
            window.lazySizes.loader.unveil(img);
            return;
          }
          if (img.dataset.src) img.src = img.dataset.src;
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        });
    }

    updateProgress() {
      if (!this.progressFill) return;
      const count = this.visibleSlides.length || 1;
      this.progressFill.style.width = `${((this.index + 1) / count) * 100}%`;
    }

    syncDesktopHeight() {
      if (!this.isStageDesktop()) {
        this.style.removeProperty('--product-media-height');
        this._lastMediaHeight = null;
        return;
      }

      const thumbs = this.querySelector('.product__media-thumbs');
      const styles = getComputedStyle(this);
      const gap =
        parseFloat(styles.getPropertyValue('--product-thumbs-gap')) ||
        16;
      const thumbsWidth = thumbs?.getBoundingClientRect().width || 0;
      const stageWidth = Math.max(
        this.getBoundingClientRect().width - thumbsWidth - gap,
        0
      );
      const ratio = this.getMediaRatio();
      const naturalHeight = ratio ? stageWidth / ratio : stageWidth;
      const maxHeight = parseFloat(styles.maxHeight);
      const height = Math.floor(
        Math.min(
          naturalHeight,
          Number.isFinite(maxHeight) ? maxHeight : naturalHeight
        )
      );

      if (
        this._lastMediaHeight != null &&
        Math.abs(this._lastMediaHeight - height) < 1
      ) {
        return;
      }

      this._lastMediaHeight = height;
      this.style.setProperty('--product-media-height', `${height}px`);
    }

    getMediaRatio() {
      const raw = getComputedStyle(this)
        .getPropertyValue('--product-media-ratio')
        .trim();
      const parts = raw.split('/').map(part => parseFloat(part));
      if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) {
        return parts[0] / parts[1];
      }
      return 3 / 4;
    }

    syncThumbsMetrics() {
      if (!this.thumbsViewport) return;
      this.thumbsViewport.style.setProperty(
        '--thumbs-viewport-height',
        `${this.thumbsViewport.clientHeight}px`
      );
    }

    scrollThumbs(direction) {
      if (!this.thumbsViewport) return;
      const horizontal = this.isHorizontalThumbs();
      const step = this.thumbStep();
      this.thumbsViewport.scrollBy({
        top: horizontal ? 0 : direction * step,
        left: horizontal ? direction * step : 0,
        behavior: 'smooth'
      });
    }

    isHorizontalThumbs() {
      if (!this.thumbsViewport) return false;
      return (
        this.thumbsViewport.scrollWidth >
        this.thumbsViewport.clientWidth + 1
      );
    }

    thumbStep() {
      const thumb = this.visibleThumbs[0];
      if (!thumb) return 0;
      const track = this.thumbsViewport.querySelector(
        '.product__media-thumbs-track'
      );
      const styles = window.getComputedStyle(
        track || this.thumbsViewport
      );
      const gap =
        parseFloat(
          this.isHorizontalThumbs()
            ? styles.columnGap || styles.gap
            : styles.rowGap || styles.gap
        ) || 0;
      return (
        (this.isHorizontalThumbs()
          ? thumb.offsetWidth
          : thumb.offsetHeight) + gap
      );
    }

    scrollThumbIntoView(animate = true) {
      const thumb = this.visibleThumbs[this.index];
      if (!thumb || !this.thumbsViewport) return;

      const thumbRect = thumb.getBoundingClientRect();
      const viewRect = this.thumbsViewport.getBoundingClientRect();
      let delta = 0;

      if (this.isHorizontalThumbs()) {
        if (thumbRect.left < viewRect.left) {
          delta = thumbRect.left - viewRect.left;
        } else if (thumbRect.right > viewRect.right) {
          delta = thumbRect.right - viewRect.right;
        }
        if (delta) {
          this.thumbsViewport.scrollBy({
            left: delta,
            behavior: animate ? 'smooth' : 'auto'
          });
        }
        return;
      }

      if (thumbRect.top < viewRect.top) {
        delta = thumbRect.top - viewRect.top;
      } else if (thumbRect.bottom > viewRect.bottom) {
        delta = thumbRect.bottom - viewRect.bottom;
      }
      if (delta) {
        this.thumbsViewport.scrollBy({
          top: delta,
          behavior: animate ? 'smooth' : 'auto'
        });
      }
    }

    updateArrows() {
      if (!this.thumbsViewport) return;
      const {
        scrollTop,
        scrollLeft,
        clientHeight,
        clientWidth,
        scrollHeight,
        scrollWidth
      } = this.thumbsViewport;
      const horizontal = this.isHorizontalThumbs();
      const atStart = horizontal ? scrollLeft <= 1 : scrollTop <= 1;
      const atEnd = horizontal
        ? scrollLeft + clientWidth >= scrollWidth - 1
        : scrollTop + clientHeight >= scrollHeight - 1;
      this.prevBtn?.toggleAttribute('disabled', atStart);
      this.nextBtn?.toggleAttribute('disabled', atEnd);
    }

    syncVideos() {
      this.visibleSlides.forEach((slide, index) => {
        const active = index === this.index;
        slide.querySelectorAll('video').forEach(video => {
          video.muted = true;
          video.playsInline = true;
          video.controls = false;
          if (active) {
            const play = video.play();
            if (play?.catch) play.catch(() => {});
          } else {
            video.pause();
            video.currentTime = 0;
          }
        });

        if (active) {
          if (typeof resumeMedia === 'function') resumeMedia(slide);
        } else if (typeof pauseAllMedia === 'function') {
          pauseAllMedia(slide);
        }
      });
    }

    setActiveMedia(id) {
      const mediaFound = this.visibleSlides.find(
        media => Number(media.dataset.mediaId) === Number(id)
      );

      if (!mediaFound) return;

      if (this.isStageDesktop() || this.slider) {
        this.goTo(Number(mediaFound.dataset.index));
        return;
      }

      const headerHeight =
        document.querySelector(this.selectors.header)?.offsetHeight ||
        0;
      window.scroll({
        top:
          mediaFound.getBoundingClientRect().top +
          window.scrollY -
          headerHeight,
        behavior: 'smooth'
      });
    }

    filterMedia({ name, value }) {
      if (name !== this.filterMediaBy) return;

      const items = this.querySelectorAll(
        `${this.selectors.mediaItem}, ${this.selectors.thumbnail}`
      );
      const matching = this.querySelectorAll(
        `${this.selectors.mediaItem}[${this.selectors.mediaColor}="${value}"], ${this.selectors.thumbnail}[${this.selectors.mediaColor}="${value}"]`
      );
      const hasFilteredMedia = matching.length !== 0;

      items.forEach(media => {
        const matches =
          media.getAttribute(this.selectors.mediaColor) === value;
        media.classList.toggle(
          'hidden',
          hasFilteredMedia && !matches
        );
      });

      this.refreshItems();
      this.goTo(0, false);
    }
  }

  customElements.define('product-media', ProductMedia);
}

document.addEventListener('DOMContentLoaded', function () {
  const radios = document.querySelectorAll(
    '.product-pendants__radio input[type="radio"]'
  );

  radios.forEach(radio => {
    radio.addEventListener('click', function () {
      const title = radio.getAttribute('data-title');
      const formattedTitle = title.replace(/\s+/g, '-').toLowerCase();
      const productMedia = document.querySelector('product-media');
      if (!productMedia) return;

      const candidateSlides =
        productMedia.querySelectorAll('[data-media-item]');
      let targetIndex = 0;

      for (const slide of candidateSlides) {
        if (slide.classList.contains('hidden')) continue;
        const classMatch = slide.className.includes(formattedTitle);
        const nestedMatch = slide.querySelector(
          `[class*="media--${formattedTitle}"]`
        );

        if (classMatch || nestedMatch) {
          const dataIndex = Number(slide.dataset.index);
          if (!Number.isNaN(dataIndex)) {
            targetIndex = dataIndex;
            break;
          }
        }
      }

      productMedia.goTo(targetIndex);
    });
  });
});
